import React from 'react';

type IUseLoadMoreData<T extends any[]> = {
  data: T;
  startAt?: number;
  pageLength?: number;
  filter?: string;
  key?: string;
  totalPages?: number;
  currentPage?: number;
};

/**
 * Hook para gerenciar a paginação de dados.
 *
 * @param {IUseLoadMoreData<T>}
 *   - `data`: Matriz dos dados a serem paginados.
 *   - `pageLength`: Número opcional de elementos por página. Padrão é 10.
 *   - `startAt`: Índice opcional para começar a paginação. Padrão é 0.
 *   - `filter`: Filtro opcional para filtrar os resultados. Padrão é undefined.
 *   - `key`: Key utilizada para filtrar os resultados deve ser utilizada junto com filter. Padrão é undefined.
 *   - `totalPages`: Número total de páginas disponíveis no backend. Padrão é undefined.
 *   - `currentPage`: Página atual dos dados do backend. Padrão é undefined.
 *
 * @returns {Object}
 *   - `actualPageData`: Dados da página atual.
 *   - `handleNextPage`: Função para avançar para a próxima página.
 *   - `actualPage`: Número da página atual.
 *   - `pageLength`: Comprimento da página.
 *   - `hasMoreData`: Booleano que indica se há uma próxima página.
 */

export function useLoadMoreData<T extends any[]>({
  data,
  startAt = 0,
  pageLength = 5,
  filter = undefined,
  key = undefined,
  totalPages,
  currentPage = 1,
}: IUseLoadMoreData<T>) {
  const start = React.useMemo(() => startAt, [startAt]);
  const end = React.useMemo(() => currentPage * pageLength, [currentPage, pageLength]);

  let filteredData: T[] = data;
  if (filter && key) {
    filteredData = data.filter((item) => item[key] === filter);
  }

  const actualPageData: T = React.useMemo(
    () => filteredData.slice(start, end) as T,
    [filteredData, start, end],
  );

  const hasMoreData = React.useMemo(() => {
    if (!totalPages) return false;
    return currentPage < totalPages;
  }, [totalPages, currentPage]);

  return {
    actualPageData,
    currentPage,
    pageLength,
    hasMoreData,
  };
}
